import React, { useState, useEffect } from 'react';
import { makeStyles } from "@mui/styles";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Input from "@mui/material/Input";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { blue } from "@mui/material/colors";
import { DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  /*formControl: {
    margin: theme.spacing(3),
  },*/
  group: {
    margin: `${theme.spacing(1)} 0`,
  },
  selectInput: {
    backgroundColor: "transparent",
  },
  formControl: {
    flexDirection: "column",
    margin: theme.spacing(3),
    width: "calc(100% - 48px)",
  },
  timezoneContainer: {
    marginTop: theme.spacing(1),
    zIndex: 10,
  },
  innerContainer: {
    borderRadius: 8,
    borderColor: "lightgray",
    borderWidth: 3,
    borderStyle: "solid",
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  option: {
    display: "inline-block",
    width: "100%",
    marginTop: "15px",
    marginBottom: "15px",
    borderBottom: "1px solid lightgray",
    "&:last-child": {
      borderBottom: 0,
    },
  },
}));

const WindowLabel = (props) => {
  const { showWarning = true } = props;
  return (
    <FormLabel
      component="legend"
      style={{
        fontSize: 14,
        fontStyle: "italic",
        marginTop: 12,
      }}
    >
      {!showWarning ? (
        "Window"
      ) : (
        <>
          Window (maximum)
          <Tooltip title="Based upon the event rates, you may not be able to use the full window requested.">
            <InfoIcon fontSize="small" style={{ color: blue[500] }} />
          </Tooltip>
        </>
      )}
    </FormLabel>
  );
};

const TimeDurationField = (props) => {
  const { timeframe, fieldId, errors, setOptions, validations, validate } =
    props;
  return (
    <TextField
      variant="standard"
      error={errors["timeDurationError"]}
      id={fieldId}
      label="Length"
      value={timeframe["length.value"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.value": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "number");
        }
      }}
      type="number"
      InputLabelProps={{ shrink: true }}
      margin="normal"
      style={{ width: "50%" }} />
  );
};

const TimeUnitsField = (props) => {
  const classes = useStyles();
  const { timeframe, fieldId, setOptions, validations, validate } = props;
  return (
    <TextField
      id={fieldId}
      select
      label="Units"
      value={timeframe["length.units"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.units": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "units");
        }
      }}
      margin="normal"
      variant="filled"
      style={{ marginLeft: 6, marginTop: 8, width: "50%" }}
      SelectProps={{
        className: classes.selectInput,
      }}
    >
      <MenuItem key="minutes" value="minutes">
        Minutes
      </MenuItem>
      <MenuItem key="hours" value="hours">
        Hours
      </MenuItem>
      {!validate && (
        <MenuItem key="days" value="days">
          Days
        </MenuItem>
      )}
    </TextField>
  );
};

const TimeframeRecurOption = (props) => {

  const { options, setOptions } = props;
  const [error, setError] = useState(false);
  const classes = useStyles();
  const timeframe = options.timeframe;
  const timezone = options.timezone;
  //console.log("props are",props);
  //check for fixedstart and fixedend
  /*if(options?.["recur"] === "0") {
    if(options?.start && !options?.fixedstart) {
      options.fixedstart = options.start;
    }
    if(options?.end && !options?.fixedend) {
      options.fixedend = options.end;
    }
  }*/
  // How Often (Schedule)
  const ctime = new Date()
  const [frequencyType, setFrequencyType] = useState(options?.["frequency-type"] ?? 'daily');
  const [selectedDays, setSelectedDays] = useState(options?.["selected-days"] ?? [ctime.getDay()]);
  const [selectedHour, setSelectedHour] = useState(options?.["selected-hour"] ?? 0);
  const [selectedMinute, setSelectedMinute] = useState(options?.["selected-minute"] ?? 0);
  const [selectedDate, setSelectedDate] = useState(ctime.getDate());
  const [selectedMonth, setSelectedMonth] = useState(ctime.getMonth()); // April
  const [cronExpression, setCronExpression] = useState('0 9 15 4 *');
  
  // What - Clear 3-Step Approach with Precise Timing
  const [lookbackAmount, setLookbackAmount] = useState(options?.["lookbackamount"] ?? 1);
  const [lookbackUnit, setLookbackUnit] = useState(options?.["lookbackunit"] ?? 'years');
  const [startSnap, setStartSnap] = useState('exact');
  const [forwardAmount, setForwardAmount] = useState(options?.["forwardamount"] ?? 12);
  const [forwardUnit, setForwardUnit] = useState(options?.["forwardunit"] ?? 'months');
  const [endSnap, setEndSnap] = useState('exact');
  
  // Configuration
  const [weekStartDay, setWeekStartDay] = useState('monday'); // 'monday' or 'sunday'
  
  // What - Aggregation
  const [aggregationUnit, setAggregationUnit] = useState('month');

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    //{ value: 'monthly', label: 'Monthly' },
    //{ value: 'yearly', label: 'Yearly' }
  ];

  const timeUnits = ['hours', 'days', 'weeks', 'months', 'years'];
  
  const aggregationUnits = [
    { value: 'hour', label: 'Hourly' },
    {value: 'shift', label: 'By Shift'},
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'year', label: 'Yearly' }
  ];

  const weekDays = [
    { value: 1, label: 'Mon', short: 'M' },
    { value: 2, label: 'Tue', short: 'T' },
    { value: 3, label: 'Wed', short: 'W' },
    { value: 4, label: 'Thu', short: 'T' },
    { value: 5, label: 'Fri', short: 'F' },
    { value: 6, label: 'Sat', short: 'S' },
    { value: 0, label: 'Sun', short: 'S' }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate snap options - all time units available
  const getAllSnapOptions = () => {
    return [
      { value: 'exact', label: 'Exact time' },
      // Hours
      { value: 'current-hour-start', label: 'Start of current hour' },
      { value: 'current-hour-end', label: 'End of current hour' },
      { value: 'previous-hour-start', label: 'Start of previous hour' },
      { value: 'previous-hour-end', label: 'End of previous hour' },
      { value: 'next-hour-start', label: 'Start of next hour' },
      { value: 'next-hour-end', label: 'End of next hour' },
      // Days
      { value: 'current-day-start', label: 'Start of current day' },
      { value: 'current-day-end', label: 'End of current day' },
      { value: 'previous-day-start', label: 'Start of previous day' },
      { value: 'previous-day-end', label: 'End of previous day' },
      { value: 'next-day-start', label: 'Start of next day' },
      { value: 'next-day-end', label: 'End of next day' },
      // Weeks
      { value: 'current-week-start', label: 'Start of current week' },
      { value: 'current-week-end', label: 'End of current week' },
      { value: 'previous-week-start', label: 'Start of previous week' },
      { value: 'previous-week-end', label: 'End of previous week' },
      { value: 'next-week-start', label: 'Start of next week' },
      { value: 'next-week-end', label: 'End of next week' },
      // Months
      { value: 'current-month-start', label: 'Start of current month' },
      { value: 'current-month-end', label: 'End of current month' },
      { value: 'previous-month-start', label: 'Start of previous month' },
      { value: 'previous-month-end', label: 'End of previous month' },
      { value: 'next-month-start', label: 'Start of next month' },
      { value: 'next-month-end', label: 'End of next month' },
      // Years
      { value: 'current-year-start', label: 'Start of current year' },
      { value: 'current-year-end', label: 'End of current year' },
      { value: 'previous-year-start', label: 'Start of previous year' },
      { value: 'previous-year-end', label: 'End of previous year' },
      { value: 'next-year-start', label: 'Start of next year' },
      { value: 'next-year-end', label: 'End of next year' }
    ];
  };

  // Helper function to get start of week based on configuration
  const getWeekStart = (date) => {
    const result = new Date(date);
    const dayOfWeek = result.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    let daysFromStart;
    if (weekStartDay === 'monday') {
      daysFromStart = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0 offset
    } else {
      daysFromStart = dayOfWeek; // Sunday = 0 offset
    }
    
    result.setDate(result.getDate() - daysFromStart);
    result.setHours(0, 0, 0, 0);
    return result;
  };

  const getWeekStartMoment = (result) => {
    const dayOfWeek = result.day(); // 0 = Sunday, 1 = Monday, etc.
    
    let daysFromStart;
    if (weekStartDay === 'monday') {
      daysFromStart = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0 offset
    } else {
      daysFromStart = dayOfWeek; // Sunday = 0 offset
    }
    
    result.day(daysFromStart);
    result.hours(0).minutes(0).seconds(0).milliseconds(0);
    return result;
  };

  // Helper function to get end of week based on configuration
  const getWeekEnd = (date) => {
    const result = new Date(date);
    const dayOfWeek = result.getDay();
    
    let daysToEnd;
    if (weekStartDay === 'monday') {
      daysToEnd = dayOfWeek === 0 ? 0 : 7 - dayOfWeek; // Sunday is end
    } else {
      daysToEnd = 6 - dayOfWeek; // Saturday is end
    }
    
    result.setDate(result.getDate() + daysToEnd);
    result.setHours(23, 59, 59, 999);
    return result;
  };

  // Helper function to apply snap with precise timing
  const applySnap = (date, snap) => {
    if (snap === 'exact') return new Date(date);
    
    const result = new Date(date);
    const [relative, unit, boundary] = snap.split('-'); // e.g., 'previous-month-start'
    
    // First apply relative offset
    switch (relative) {
      case 'previous':
        switch (unit) {
          case 'hour': result.setHours(result.getHours() - 1); break;
          case 'day': result.setDate(result.getDate() - 1); break;
          case 'week': result.setDate(result.getDate() - 7); break;
          case 'month': result.setMonth(result.getMonth() - 1); break;
          case 'year': result.setFullYear(result.getFullYear() - 1); break;
        }
        break;
      case 'next':
        switch (unit) {
          case 'hour': result.setHours(result.getHours() + 1); break;
          case 'day': result.setDate(result.getDate() + 1); break;
          case 'week': result.setDate(result.getDate() + 7); break;
          case 'month': result.setMonth(result.getMonth() + 1); break;
          case 'year': result.setFullYear(result.getFullYear() + 1); break;
        }
        break;
      // 'current' doesn't need offset
    }
    // Then apply boundary alignment with precise timing
    if (boundary === 'start') {
      switch (unit) {
        case 'hour':
          result.setMinutes(0, 0, 0);
          break;
        case 'day':
          result.setHours(0, 0, 0, 0);
          break;
        case 'week':
          return getWeekStart(result);
        case 'month':
          result.setDate(1);
          result.setHours(0, 0, 0, 0);
          break;
        case 'year':
          result.setMonth(0, 1);
          result.setHours(0, 0, 0, 0);
          break;
      }
    } else if (boundary === 'end') {
      switch (unit) {
        case 'hour':
          result.setMinutes(59, 59, 999);
          break;
        case 'day':
          result.setHours(23, 59, 59, 999);
          break;
        case 'week':
          return getWeekEnd(result);
        case 'month':
          result.setMonth(result.getMonth() + 1, 0); // Last day of month
          result.setHours(23, 59, 59, 999);
          break;
        case 'year':
          result.setMonth(11, 31);
          result.setHours(23, 59, 59, 999);
          break;
      }
    }
    
    return result;
  };

    const applySnapWithMoment = (date, snap) => {
    if (snap === 'exact') return date;
    const tzz = timezone ? timezone : Intl.DateTimeFormat().resolvedOptions().timeZone
    const result = moment.tz(date,tzz);
    const [relative, unit, boundary] = snap.split('-'); // e.g., 'previous-month-start'
    // First apply relative offset
    switch (relative) {
      case 'previous':
        switch (unit) {
          case 'hour': result.setHours(result.getHours() - 1); break;
          case 'day': result.setDate(result.getDate() - 1); break;
          case 'week': result.setDate(result.getDate() - 7); break;
          case 'month': result.setMonth(result.getMonth() - 1); break;
          case 'year': result.setFullYear(result.getFullYear() - 1); break;
        }
        break;
      case 'next':
        switch (unit) {
          case 'hour': result.setHours(result.getHours() + 1); break;
          case 'day': result.setDate(result.getDate() + 1); break;
          case 'week': result.setDate(result.getDate() + 7); break;
          case 'month': result.setMonth(result.getMonth() + 1); break;
          case 'year': result.setFullYear(result.getFullYear() + 1); break;
        }
        break;
      // 'current' doesn't need offset
    }
    // Then apply boundary alignment with precise timing
    if (boundary === 'start') {
      switch (unit) {
        case 'hour':
          result.minutes(0).seconds(0).milliseconds(0);
          break;
        case 'day':
          result.hours(0).minutes(0).seconds(0).milliseconds(0);
          break;
        case 'week':
          return getWeekStartMoment(result);
        case 'month':
          result.date(1);
          result.hours(0).minutes(0).seconds(0).milliseconds(0);
          break;
        case 'year':
          result.month(0).date(1);
          result.hours(0).minutes(0).seconds(0).milliseconds(0);
          break;
      }
    } else if (boundary === 'end') {
      switch (unit) {
        case 'hour':
          result.setMinutes(59, 59, 999);
          break;
        case 'day':
          result.setHours(23, 59, 59, 999);
          break;
        case 'week':
          return getWeekEnd(result);
        case 'month':
          result.setMonth(result.getMonth() + 1, 0); // Last day of month
          result.setHours(23, 59, 59, 999);
          break;
        case 'year':
          result.setMonth(11, 31);
          result.setHours(23, 59, 59, 999);
          break;
      }
    }
    
    return result;
  };


  // Helper function to subtract time units
  const subtractTimeUnit = (date, amount, unit) => {
    let result = new Date(date);
    
    switch (unit) {
      case 'hours':
        //result.setHours(result.getHours() - amount);
        result = result.valueOf() - amount*60*60*1000
        break;
      case 'days':
        result.setDate(result.getDate() - amount);
        break;
      case 'weeks':
        result.setDate(result.getDate() - (amount * 7));
        break;
      case 'months':
        result.setMonth(result.getMonth() - amount);
        break;
      case 'years':
        result.setFullYear(result.getFullYear() - amount);
        break;
    }
    
    return result;
  };

  // Helper function to add time units
  const addTimeUnit = (date, amount, unit) => {
    let result = new Date(date);
    
    switch (unit) {
      case 'hours':
        //result.setHours(result.getHours() + amount);
        result = (result.valueOf() + (amount*60*60*1000))
        break;
      case 'days':
        result.setDate(result.getDate() + amount);
        break;
      case 'weeks':
        result.setDate(result.getDate() + (amount * 7));
        break;
      case 'months':
        result.setMonth(result.getMonth() + amount);
        break;
      case 'years':
        result.setFullYear(result.getFullYear() + amount);
        break;
    }
    
    return result;
  };

  // Calculate the actual date range using the 3-step approach
  const calculateDateRange = (runDate) => {
    // Step 1: Go back X time units from run time
    const lookbackPoint = subtractTimeUnit(runDate, lookbackAmount, lookbackUnit);
    
    // Step 2: Apply start snap (independent of lookback unit)
    const snapMap = {
      "hours":"current-day-start",
      "days":"current-day-start",
      "weeks":"current-week-start",
      "months":"current-month-start",
      "years":"current-month-start"
    }
    //const startDate = applySnapWithMoment(lookbackPoint, options["beginat"] === "0" ? "exact" :startSnap);
    const startDate = applySnapWithMoment(lookbackPoint, options["beginat"] === "0" ? "exact" : (snapMap?.lookbackUnit ?? "current-day-start"));
    // Step 3: Go forward Y time units from start date, then apply end snap (independent of forward unit)
    const forwardPoint = addTimeUnit(startDate, forwardAmount, forwardUnit);
    //const endDate = applySnapWithMoment(forwardPoint, options["beginat"] === "0" ? "exact" :endSnap);
    //not actually doing endSnap anymore.. always resort to "exact"
    const endDate = applySnapWithMoment(forwardPoint, options["beginat"] === "0" ? "exact" :"exact");
    //console.log("runtimes",runDate,lookbackPoint,lookbackAmount, lookbackUnit,forwardAmount, forwardUnit,endSnap,startDate,options["beginat"],forwardPoint,endDate)
    return { start: startDate, end: endDate };
  };
  useEffect(() => {
    const val = lookbackUnit === "hours" ? (parseFloat(lookbackAmount) || 1) : (parseInt(lookbackAmount) || 1);
    setLookbackAmount(val);
    setOptions({"lookbackamount":val.toString()});
    //console.log("change in lookback units.. coverting appropriate value",lookbackAmount,lookbackUnit)
  },[lookbackUnit])


  useEffect(() => {
    const val = forwardUnit === "hours" ? (parseFloat(forwardAmount) || 1) : (parseInt(forwardAmount) || 1);
    setForwardAmount(val);
    setOptions({"forwardamount":val.toString()});
    //console.log("change in forward units.. coverting appropriate value",forwardAmount,forwardUnit)
  },[forwardUnit])

  
  // Auto-adjust defaults based on frequency
  useEffect(() => {
    const defaults = {
      daily: { 
        lookbackAmount: 1, lookbackUnit: 'days', startSnap: 'current-day-start',
        forwardAmount: 1, forwardUnit: 'days', endSnap: 'current-day-start',
        aggregation: 'hour' 
      },
      weekly: { 
        lookbackAmount: 1, lookbackUnit: 'weeks', startSnap: 'current-week-start',
        forwardAmount: 1, forwardUnit: 'weeks', endSnap:'current-week-start',
        aggregation: 'day' 
      },
      monthly: { 
        lookbackAmount: 1, lookbackUnit: 'months', startSnap: 'current-month-start',
        forwardAmount: 1, forwardUnit: 'months', endSnap: 'current-month-start',
        aggregation: 'day' 
      },
      yearly: { 
        lookbackAmount: 1, lookbackUnit: 'years', startSnap: 'current-month-start',
        forwardAmount: 12, forwardUnit: 'months', endSnap: 'current-month-start',
        aggregation: 'month' 
      }
    };
    
    if (defaults[frequencyType]) {
      //console.log("in useeffect of freqtype",frequencyType,defaults[frequencyType]);
      const def = defaults[frequencyType];
      if (!options?.["lookbackamount"]) {
        //console.log("set default lookbackamount",def.lookbackAmount)
        setLookbackAmount(def.lookbackAmount);
      }
      if (!options?.["lookbackunit"]) {
        //console.log("set default lookbackunit",def.lookbackUnit)
        setLookbackUnit(def.lookbackUnit);
      }
      setStartSnap(def.startSnap);
      if (!options?.["forwardamount"]) {
        //console.log("set default forwardamount",def.forwardAmount)
        setForwardAmount(def.forwardAmount);
      }
      if (!options?.["forwardunit"]) {
        //console.log("set default forwardunit",def.forwardUnit)
        setForwardUnit(def.forwardUnit);
      }
      setEndSnap(def.endSnap);
      //setAggregationUnit(def.aggregation);
    }
  }, [frequencyType]);

  // Generate cron expression
  useEffect(() => {
    let cron = '';
    let recurdays = 1;
    switch (frequencyType) {
      case 'daily':
        cron = `${selectedMinute} ${selectedHour} * * *`;
        recurdays = 1
        break;
      case 'weekly':
        const days = selectedDays.length > 0 ? selectedDays.join(',') : '*';
        recurdays = 7
        cron = `${selectedMinute} ${selectedHour} * * ${days}`;
        break;
      case 'monthly':
        cron = `${selectedMinute} ${selectedHour} ${selectedDate} * *`;
        break;
      case 'yearly':
        cron = `${selectedMinute} ${selectedHour} ${selectedDate} ${selectedMonth} *`;
        break;
      default:
        cron = '*/1 * * * *';
        recurdays = 1
    }
    let now = new Date()
    const ctime = new Date(now.getFullYear(), now.getMonth() , now.getDate() , selectedHour, selectedMinute, 0, 0);
    const mtime = moment();
    const tzz = timezone ? timezone : Intl.DateTimeFormat().resolvedOptions().timeZone
    const mtz = mtime.tz(tzz).hours(selectedHour).minutes(selectedMinute).second(0).millisecond(0);
    //console.log("momemt times are",tzz,mtime,mtz);
    //console.log("ctime is",ctime,selectedDays,(Math.max(selectedDays) - now.getDay()),mtime.valueOf(),mtz.valueOf());
    setOptions({ "recur-days": recurdays, "execute-after": frequencyType === "weekly" ? mtz.valueOf() + (86400*1000*(Math.max(selectedDays) - now.getDay())) :mtz.valueOf() });
    //setOptions({ "recur-days": recurdays, "execute-after": frequencyType === "weekly" ? ctime.valueOf() + (86400*1000*Math.abs(Math.max(selectedDays) - now.getDay())) :ctime.valueOf() });

    //setCronExpression(cron);
  }, [options?.recur,frequencyType, selectedDays, selectedHour, selectedMinute, selectedDate, selectedMonth,timezone]);

  /*const handleDayToggle = (dayValue) => {
    setSelectedDays(prev => 
      prev.includes(dayValue) 
        ? prev.filter(d => d !== dayValue)
        : [...prev, dayValue].sort()
    );
  };*/

  const getScheduleDescription = () => {
    if(options?.recur === "0") {
      return "Immediately"
    }
    const execval = moment.tz(options?.["execute-after"],timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    const att = ` at ${String(execval.hour()%12)}:${String(execval.minute()).padStart(2, '0')} ${execval.hour() > 12 ? 'PM': 'AM' }`;
    //console.log("execute at",options?.["execute-after"],timezone,execval.hour(),execval.minute());
    switch (frequencyType) {
      case 'daily':
        return `Daily${att}`;
      case 'weekly':
        const dayNames = selectedDays.map(d => weekDays.find(wd => wd.value === d)?.label).join(', ');
        return `Weekly on ${dayNames || 'all days'}${att}`;
      case 'monthly':
        return `Monthly on day ${selectedDate}${att}`;
      case 'yearly':
        return `Yearly on ${months[selectedMonth - 1]} ${selectedDate}${att}`;
      default:
        return '';
    }
  };

  const getDataFlowDescription = () => {
    const startSnapLabel = getAllSnapOptions().find(opt => opt.value === startSnap)?.label || startSnap;
    const endSnapLabel = getAllSnapOptions().find(opt => opt.value === endSnap)?.label || endSnap;

    return {
      step1: `Go back ${lookbackAmount} ${lookbackUnit}`,
      step2: startSnapLabel,
      step3: `Forward ${forwardAmount} ${forwardUnit}, ${endSnapLabel}`
    };
  };

  const getExampleScenario = () => {
    const now = new Date();
    const mockRunDate = new Date(now.getFullYear(), now.getMonth() , now.getDate(), selectedHour, selectedMinute);
    //const dateRange = calculateDateRange(mockRunDate);
    const dateRange = calculateDateRange(options?.["execute-after"])
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: date.getHours() !== 0 || date.getMinutes() !== 0 ? 'numeric' : undefined,
        minute: date.getMinutes() !== 0 ? '2-digit' : undefined
      });
    };
    if (options?.["recur"] === "1" && (options?.recurstart !== dateRange.start.valueOf() || options?.recurend !== dateRange.end.valueOf())) {
      setOptions({ "recurstart": dateRange.start.valueOf(),"recurend": dateRange.end.valueOf() })
    } 
    //console.log("example",options?.fixedstart,options?.recurstart,dateRange.start);
    if(options?.["sch-desc"] !== getScheduleDescription()) {
      setOptions({"sch-desc":getScheduleDescription()});
    }
    return {};
    /*return {
      start: options?.["recur"] !== "1" ? formatDate(new Date(options?.fixedstart)): formatDate(dateRange.start),
      end: options?.["recur"] !== "1" ? formatDate(new Date(options?.fixedend)): formatDate(dateRange.end),
      aggregation: aggregationUnits.find(unit => unit.value === aggregationUnit)?.label || 'Daily'
    };*/
  };

  const example = getExampleScenario();
  const dataFlow = getDataFlowDescription();

  return (
    <FormControl variant="standard" fullWidth={true} className={classes.formControl}>
      <FormLabel component="legend">Export Schedule</FormLabel>
      <FormLabel style={{
                        paddingTop: 10,
                        "font-size": "0.8rem",
                        "font-weight": 400,
                        "line-height": 1,
                        "letter-spacing": "0.00938em"
              }}>Export requests are processed as compute is available
      </FormLabel>
      <hr></hr>
      <FormControl variant="standard">
        <RadioGroup
        row
        aria-labelledby="demo-radio-buttons-group-label"
        defaultValue="0"
        name="recur"
        value={options["recur"] ?? "0"}
        
      >
          <FormControlLabel value="0" control={<Radio onChange={(event) => {
                  const value = event.target.value;
                  setOptions({
                    "recur": value,
                  });
                }}/>} label="Immediately" />
          <FormControlLabel value="1" control={<Radio onChange={(event) => {
                  const value = event.target.value;
                  setOptions({
                    "recur": value,
                  });
                }}/>} label="Recurring" />
        </RadioGroup>
      </FormControl>
      { options["recur"] === "1" &&
      <FormControl variant="standard" fullWidth={true} style={{ flexDirection: "row" }}>
         <FormControl
           variant="standard"
           margin="dense"
           //fullWidth={true}
           style={{ marginTop: 11, width:"33%", marginRight:11 }}>
      <InputLabel >
        Export Frequency
      </InputLabel>
      <Select
        variant="standard"
        labelId={`period-range-selection`}
        value={frequencyType}
        onChange={(event) => {
                  const value = event.target.value;
                  setFrequencyType(value);
                  setOptions({
                    "frequency-type": value,
                  });
                }}
        input={<Input />}
        /*renderValue={(selected) => (
          <div style={{ fontSize: ".75rem" }}>{selected.join(",")}</div>
        )}*/>
        {frequencyOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
      </Select>
      </FormControl>
      {frequencyType === 'weekly' && (
      <FormControl
        variant="standard"
        margin="dense"
        //fullWidth={true}
        style={{ marginTop: 11, width:"25%",marginRight:11   }}>
            <InputLabel >
            On
          </InputLabel>
          <Select
            variant="standard"
            labelId={`period-range-selection`}
            value={selectedDays[0]}
            onChange={(event) => {
                  const value = event.target.value;
                  setSelectedDays([value]);
                  setOptions({
                    "selected-days": [value],
                  });
                }}
            //onChange={(e) => setSelectedDays([e.target.value])}
            input={<Input />}
            /*renderValue={(selected) => (
              <div style={{ fontSize: ".75rem" }}>{selected.join(",")}</div>
            )}*/>
            {weekDays.map(day => (
          <MenuItem
            key={day.value}
            value = {day.value}
            
          >
            {day.label}
            </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TimePicker
        key={timezone || "local"}
        error={false}
        id="starttime"
        label="Scheduled Export Time"
        value={
                timezone
                  ? moment.tz(options?.["execute-after"], timezone)
                  : moment.tz(options?.["execute-after"], Intl.DateTimeFormat().resolvedOptions().timeZone)
              }
        onChange={(dt) => {
          //console.log("selected time is",dt,dt.valueOf(),dt.hour(),dt.minute())
          setSelectedHour(dt.hour());
          setSelectedMinute(dt.minute());
          setOptions({
                    "selected-hour": dt.hour(),
                    "selected-minute": dt.minute(),
                  });
        }}
        style={{ width: "100%", maxWidth: "100%", margin: 6 }}
      />
      
      </FormControl>
      
      }
      <hr></hr>
      { options["recur"] === "2" && 
      <div className="space-y-3">
      <Grid item container spacing={1} xs={12}>
      <FormControl
        variant="standard"
        margin="dense"
        //fullWidth={true}
        style={{ marginTop: 11 }}>
      <InputLabel >
        Frequency
      </InputLabel>
      <Select
        variant="standard"
        labelId={`period-range-selection`}
        value={frequencyType}
        onChange={(e) => setFrequencyType(e.target.value)}
        input={<Input />}
        /*renderValue={(selected) => (
          <div style={{ fontSize: ".75rem" }}>{selected.join(",")}</div>
        )}*/>
        {frequencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
      </Select>
      </FormControl>
      </Grid>
      <Grid item container spacing={1} xs={12}>
      <label className="block text-xs font-medium text-gray-700 mb-1" style={{ marginTop: 11 }}>Time</label>
      </Grid>
      <Grid item container spacing={1} xs={12}>
        
        <Grid item xs={1}>
          <FormControl variant="standard">
            <TextField
              variant="standard"
              id="hour"
              type="number"
              min="0"
              max="23"
              //label="Filename"
              value={selectedHour}
              onChange={(e) => setSelectedHour(parseInt(e.target.value) || 0)} />
          </FormControl>
        </Grid>
        <Grid item xs={1}>
          <FormControl variant="standard">
            <TextField
              variant="standard"
              id="minute"
              type="number"
              min="0"
              max="59"
              value={selectedMinute}
              onChange={(e) => setSelectedMinute(parseInt(e.target.value) || 0)} />
          </FormControl>
        </Grid>
      </Grid>
        {frequencyType === 'weekly' && (
          <div>

            <div className="grid grid-cols-7 gap-1">
              <FormControl
                variant="standard"
                margin="dense"
                //fullWidth={true}
                style={{ marginTop: 11 }}>
                  <InputLabel >
                  On
                </InputLabel>
                <Select
                  variant="standard"
                  labelId={`period-range-selection`}
                  value={selectedDays[0]}
                  onChange={(e) => setSelectedDays([e.target.value])}
                  input={<Input />}
                  /*renderValue={(selected) => (
                    <div style={{ fontSize: ".75rem" }}>{selected.join(",")}</div>
                  )}*/>
                  {weekDays.map(day => (
                <option
                  key={day.value}
                  value = {day.value}
                  
                >
                  {day.label}
                  </option>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        {(frequencyType === 'monthly' || frequencyType === 'yearly') && (
            <FormControl variant="standard">
            <TextField
              variant="standard"
              label="Day"
              type="number"
              min="1"
              max="31"
              value={selectedDate}
              onChange={(e) => setSelectedDate(parseInt(e.target.value) || 1)} />
          </FormControl>
        )}
        
        {frequencyType === 'yearly' && (

          
          <div>

            <FormControl
              variant="standard"
              margin="dense"
              fullWidth={true}
              style={{ marginTop: 11 }}>
            <InputLabel >
              Month
            </InputLabel>
            <Select
              variant="standard"
              //labelId={`period-range-selection`}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              input={<Input />}
              /*renderValue={(selected) => (
                <div style={{ fontSize: ".75rem" }}>{selected.join(",")}</div>
              )}*/>
              {months.map((month, index) => (
                        <option key={month} value={index + 1}>
                          {month}
                        </option>
                      ))}
            </Select>
            </FormControl>
            
          </div>
        )}
      </div>
      }
      {/* 2. What Data */}
      <div className={classes.option}>
      </div>
      <FormLabel component="legend">Data to Export</FormLabel>
      <hr></hr>
      {options?.recur === "0" && (
          <Grid container direction="row" spacing={1}>
            <Grid item xs={4}>
              <DateTimePicker
                //openTo='hour'
                //views = 'hour'
                key={"starttime"}
                //error={!(options?.["recur"] === "0" && (options?.fixedstart ?? options?.start)) ?? true}
                id="starttime"
                label="Start"
                value={
                  timezone
                    ? (options?.fixedstart ?? options?.start ? moment.tz(options?.["recur"] === "0" && (options?.fixedstart ?? options?.start), timezone) : null)
                    : (options?.fixedstart ?? options?.start ?moment.tz(options?.["recur"] === "0" && (options?.fixedstart ?? options?.start),Intl.DateTimeFormat().resolvedOptions().timeZone) : null)
                }
                onChange={(dt) => {
                  console.log("start dt is",dt)
                  props.setOptions({
                     fixedstart: dt.seconds(0).millisecond(0).valueOf() ,
                  });
                }}
                style={{ width: "100%", maxWidth: "100%", margin: 6 }}
              />
            </Grid>
            <Grid item xs={4}>
              <DateTimePicker
                  key={"end-time"}
                  //error={!(options?.["recur"] === "0" && (options?.fixedend ?? options?.end))}
                  id="endtime"
                  label="End"
                  value={
                    timezone
                      ? (options?.fixedend ?? options?.end ? moment.tz(options?.["recur"] === "0" && (options?.fixedend ?? options?.end), timezone) : null)
                      : (options?.fixedend ?? options?.end ? moment.tz(options?.["recur"] === "0" && (options?.fixedend ?? options?.end),Intl.DateTimeFormat().resolvedOptions().timeZone) : null)
                  }
                  onChange={(dt) => {
                    console.log("end dt is",dt,dt.valueOf())
                    props.setOptions({ fixedend: dt.seconds(0).millisecond(0).valueOf()});
                  }}
                  style={{ width: "100%", maxWidth: "100%", margin: 6 }}
                />
            </Grid>
          </Grid>
        
      )}
      {options?.recur === "1" && (
        <>
        <FormLabel style={{
                  paddingTop: 10,
                  "font-size": "0.8rem",
                  "font-weight": 400,
                  "line-height": 1,
                  "letter-spacing": "0.00938em"
        }}>
            Include data starting
        </FormLabel>
        <FormControl
          variant="standard"
          style={{ flexDirection: "row", marginRight: 10, width: "100%" }}>
        <TextField
          variant="standard"
          //error={errors["timeDurationError"]}
          //id={fieldId}
          //label="Length"
          value={lookbackUnit === "hours" ? parseFloat(options?.["lookbackamount"] ?? lookbackAmount) : parseInt(options?.["lookbackamount"] ?? lookbackAmount)}
          onChange={(e) => {
            const val = lookbackUnit === "hours" ? (parseFloat(e.target.value) || 1) : (parseInt(e.target.value) || 1);
            setLookbackAmount(val);
            setOptions({"lookbackamount":val.toString()});
          }}
          type="number"
          //InputLabelProps={{ shrink: true }}
          margin="dense"
          style={{ width: "8%", marginRight: 10}} />
        <Select
          //id={fieldId}
          //label="Units"
          value={options?.["lookbackunit"] ?? lookbackUnit}
          onChange={(e) => {
                const val = e.target.value;
                setLookbackUnit(val);
                setOptions({"lookbackunit":val});
              }}
          input={<Input />}
          margin="dense"
          variant="standard"
          style={{ marginLeft: 6, marginTop: 0, width: "12%" }}
          SelectProps={{
            className: classes.selectInput,
          }}
        >
          {timeUnits.map(unit => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
        <FormLabel style={{ marginLeft: 6, marginTop: 20 }}>before scheduled export time</FormLabel>
        </FormControl>
        <FormControlLabel
            name="snaplabel"
            label={"Snap Export window to start of "+lookbackUnit.slice(0,lookbackUnit.length - 1)}
            control={
              <Checkbox
                checked={(options?.["beginat"] ?? "0") === "1"} //default is off as per Brian
                onChange={(event) => {
                  const value = event.target.checked;
                  setOptions({
                    "beginat": value ? "1" : "0",
                  });
                }}
                name="snap?"
              />
            }
        />
        <hr></hr>
        <FormLabel style={{
                  paddingTop: 10,
                  "font-size": "0.8rem",
                  "font-weight": 400,
                  "line-height": 1,
                  "letter-spacing": "0.00938em"
        }}>
            Duration of data export window
        </FormLabel>
        <FormControl
          variant="standard"
          style={{ flexDirection: "row", marginRight: 10, width: "100%" }}>
        <TextField
          variant="standard"
          //error={errors["timeDurationError"]}
          //id={fieldId}
          //label="Length"
          value={forwardUnit === "hours" ? parseFloat(options?.["forwardamount"] ?? forwardAmount) : parseInt(options?.["forwardamount"] ?? forwardAmount)}
          onChange={(e) => {
            const val = forwardUnit === "hours" ? (parseFloat(e.target.value) || 1) : (parseInt(e.target.value) || 1);
            setForwardAmount(val);
            setOptions({"forwardamount":val.toString()});
          }}
          type="number"
          //InputLabelProps={{ shrink: true }}
          margin="dense"
          style={{ width: "8%", marginRight: 10}} />
        <Select
          //id={fieldId}
          //label="Units"
          value={options?.["forwardunit"] ?? forwardUnit}
          onChange={(e) => {
                const val = e.target.value;
                setForwardUnit(val);
                setOptions({"forwardunit":val});
              }}
          input={<Input />}
          margin="dense"
          variant="standard"
          style={{ marginLeft: 6, marginTop: 0, width: "12%" }}
          SelectProps={{
            className: classes.selectInput,
          }}
        >
          {timeUnits.map(unit => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
        </FormControl>
        </>
      )}
      {options?.recur === "2" &&
      (
      <div className="space-y-6">
        {/* Go Back */}
        <Grid item container spacing={1} xs={12}>
          <Grid item container xs={6}>
          <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-3">Go back</h4>
          <div className="grid grid-cols-2 gap-3">
            <input
              //type="number"
              //min="1"
              value={lookbackAmount}
              onChange={(e) => setLookbackAmount(parseFloat(e.target.value) || 1)}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <select 
                value={lookbackUnit}
                onChange={(e) => setLookbackUnit(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {timeUnits.map(unit => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
              <RadioGroup
                row
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="1"
                name="beginat"
                value={options["beginat"] ?? "1"}
          
              >
                <FormControlLabel value="0" control={<Radio onChange={(event) => {
                        const value = event.target.value;
                        setOptions({
                          "beginat": value,
                        });
                      }}/>} label="Begin at Scheduled Time" />
                <FormControlLabel value="1" control={<Radio onChange={(event) => {
                        const value = event.target.value;
                        setOptions({
                          "beginat": value,
                        });
                      }}/>} label={"Begin at Start of "+lookbackUnit.slice(0,lookbackUnit.length - 1)} />
                </RadioGroup>
          </div>
        </div>
          </Grid>
          { false && <Grid item container xs={6}>
           <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-semibold text-green-900 mb-3">Snap start to</h4>
          <div className="relative">
            <select 
              value={startSnap}
              onChange={(e) => setStartSnap(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            >
              {getAllSnapOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          </div>
          </Grid>
          }
        </Grid>

        {/* Go Forward */}
        <Grid item container spacing={1} xs={12}>
          <Grid item container xs={6}>
        <div className="bg-purple-50 p-4 rounded-lg"> 
          <h4 className="text-sm font-semibold text-purple-900 mb-3">Duration</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              //type="number"
              //min="1"
              value={forwardAmount}
              onChange={(e) => setForwardAmount(parseFloat(e.target.value) || 1)}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
            <select 
                value={forwardUnit}
                onChange={(e) => setForwardUnit(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              >
                {timeUnits.map(unit => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
          </div>
          </div>
          </Grid>
          { false && <Grid item container xs={6}>
          <div>
            <h4 className="text-sm font-semibold text-green-900 mb-3">Snap end to</h4>
            <div className="relative">
              <select 
                value={endSnap}
                onChange={(e) => setEndSnap(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              >
                {getAllSnapOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          </Grid>
          }
        </Grid>
      </div>  
      )}
      {false && (
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <p className="text-sm font-medium text-gray-600 mb-1">Schedule</p>
                <p className="text-sm text-gray-900">{getScheduleDescription()}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <p className="text-sm font-medium text-gray-600 mb-1">Data Range</p>
                <p className="text-sm text-gray-900">{example.start} to {example.end}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <p className="text-sm font-medium text-gray-600 mb-1">Aggregation</p>
                <p className="text-sm text-gray-900">{options?.timerange ?? 'day'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </FormControl>
  );
};

export default TimeframeRecurOption;
